import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImageUp, Trash2, Loader2, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { Panel } from "@/components/core/AppLayout";
import { ClientAvatar } from "./ClientAvatar";

export const ClientBranding = ({ client, onChanged }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (f) => {
    if (!f) return;
    if (!/\.(png|jpe?g|svg)$/i.test(f.name)) return toast.error("Carica un PNG, JPG o SVG");
    if (f.size > 5 * 1024 * 1024) return toast.error("Il logo supera i 5 MB");
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { const r = await api.post(`/clients/${client.id}/logo`, fd); onChanged({ ...client, logo_key: r.data.logo_key }); toast.success("Logo aggiornato"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    try { await api.delete(`/clients/${client.id}/logo`); onChanged({ ...client, logo_key: null }); toast.success("Logo rimosso"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (attivo) => {
    try { await api.patch(`/clients/${client.id}/sollecito`, { attivo }); onChanged({ ...client, sollecito_auto: attivo }); toast.success(attivo ? "Sollecito automatico attivato" : "Sollecito automatico disattivato"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel className="p-6 mt-6 grid grid-cols-2 gap-8" data-testid="client-branding">
      <div>
        <h3 className="font-display text-lg font-extrabold text-[#0A1128] mb-1">Logo del cliente</h3>
        <p className="text-sm text-slate-600 mb-4">Appare nelle schede clienti, nel portale, nella copertina del report PDF e nei solleciti. PNG, JPG o SVG, max 5 MB.</p>
        <div className="flex items-center gap-4">
          <ClientAvatar client={client} size="lg" dark testid="branding-logo-preview" />
          <input ref={ref} type="file" accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml" className="hidden" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid="branding-logo-input" />
          <Button onClick={() => ref.current.click()} disabled={busy} className="rounded-full bg-[#0A1128] hover:bg-[#E8173A] font-bold" data-testid="branding-logo-upload">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <ImageUp className="h-4 w-4 mr-1.5" />}{client.logo_key ? "Sostituisci logo" : "Carica logo"}</Button>
          {client.logo_key && <Button variant="outline" onClick={remove} className="rounded-full font-bold text-[#C8102E] border-red-200" data-testid="branding-logo-remove"><Trash2 className="h-4 w-4 mr-1" />Rimuovi</Button>}
        </div>
      </div>
      <div>
        <h3 className="font-display text-lg font-extrabold text-[#0A1128] mb-1 flex items-center gap-2"><BellRing className="h-5 w-5 text-[#E8173A]" />Sollecito automatico</h3>
        <p className="text-sm text-slate-600 mb-4">Se il portale è attivo e ci sono post «In revisione» da più di 3 giorni con pubblicazione futura, il cliente riceve un promemoria alle 10:00 (al massimo uno ogni 3 giorni, massimo 3 per post).</p>
        <label className="flex items-center gap-3 text-sm font-bold text-[#0A1128]">
          <Switch checked={client.sollecito_auto !== false} onCheckedChange={toggle} data-testid="branding-sollecito-toggle" />
          {client.sollecito_auto !== false ? "Attivo" : "Disattivato"}
        </label>
      </div>
    </Panel>
  );
};
